<template>
  <v-container>
    <div class="back-nav">
      <v-btn text :to="{ name: 'employees' }" exact>
        <v-icon left>mdi-chevron-left</v-icon>
        Back To My Employees
      </v-btn>
    </div>
    <div class="header">
      <h1 class="mb-4 page-title d-sm-inline-block">
        <v-icon>$addemployees</v-icon>
        Add Employees
      </h1>
    </div>

    <employees-preview
      v-if="previewErrors"
      :validation-errors="previewErrors"
      :employees="previewEmployees"
      :enable-save="previewHasValid"
      @reset="reset"
      @save="uploadFile(true)"
      ref="employeesPreviewChild"
    />

    <pt-sheet class="main-content" v-else>
      <employees-upload-success v-if="success" />
      <template v-else>
        <v-card-title>
          <h2 class="s">Upload bulk employee file</h2>
        </v-card-title>
        <div class="d-flex justify-space-between">
          <div>
            <ol class="parenthetical-list">
              <li>
                Download our employee monitoring bulk upload template<br />
                <file-download-button
                  class="download-button"
                  file-location="sample-upload-file.csv"
                  file-name="sample-upload-file.csv"
                  text="Download Bulk Update Template"
                >
                </file-download-button>
              </li>
              <li>
                Add your data to the template
                <ul>
                  <li>Format must be exactly the same as the template</li>
                  <li>SSN and First/Last name are required</li>
                  <li>SSN must be 9 digits</li>
                  <li>Date should be formatted as MM/DD/YYYY</li>
                  <li>
                    If using excel, make sure you export or save as a .csv
                  </li>
                </ul>
              </li>
              <li>
                Upload it below for processing
              </li>
            </ol>
            <span class="manual-upload">*You can also </span
            ><v-btn text :to="{ name: 'addEmployees' }" class="inline bold"
              >manually upload here.</v-btn
            >
          </div>
          <file-upload-dnd
            class="flex-grow-1"
            :accepted-files="{
              CSV: 'text/csv'
            }"
            :dropzone-options="dropzoneOptions"
            title="Upload Bulk Employee File"
            @input="fileId = $event"
          >
          </file-upload-dnd>
        </div>

        <div class="text-right mt-8">
          <v-btn
            class="mb-4"
            depressed
            color="primary"
            @click="uploadFile()"
            :block="$vuetify.breakpoint.xsOnly"
            :disabled="!fileId"
            :loading="loading"
          >
            Continue
          </v-btn>
        </div>
      </template>
    </pt-sheet>
  </v-container>
</template>

<script>
import FileUploadDnd from "@/components/FileUploadDnd";
import { post } from "@/services/request";
import { employeesUploadAPI } from "@/services/api";
import PtSheet from "@/components/PtSheet.vue";
import hasNotifications from "@/mixins/has-notifications";
import StaticFileDownloadButton from "@/components/providertrust/buttons/StaticFileDownloadButton";
import EmployeesUploadSuccess from "./EmployeesUploadSuccess";
import EmployeesPreview from "./EmployeesPreview";

const dropzoneOptions = {
  url: "/services/files",
  headers: {
    "user-type": "vendor",
    "directory-name": "employeeUpload"
  }
};

export default {
  name: "BulkAddEmployee",
  mixins: [hasNotifications],
  components: {
    PtSheet,
    FileUploadDnd,
    "file-download-button": StaticFileDownloadButton,
    EmployeesUploadSuccess,
    EmployeesPreview
  },
  data() {
    return {
      fileId: null,
      dropzoneOptions,
      success: false,
      previewErrors: null,
      previewEmployees: null,
      previewHasValid: false,
      loading: false
    };
  },
  methods: {
    reset() {
      this.previewErrors = null;
      this.fileId = null;
      this.loading = false;
    },
    uploadFile(validOnly) {
      const fileId = this.fileId;
      this.loading = true;

      post(employeesUploadAPI, {
        fileId,
        validOnly
      }).then(
        () => {
          console.log("Employee file submit successful, fileId: ", fileId);

          this.reset();
          this.success = true;
        },
        error => {
          console.error("Employee file submit failed, fileId: ", fileId);
          this.loading = false;
          //since child component is also running this method, we need to unset their property too
          // (when the child exists)
          if (this.$refs.employeesPreviewChild) {
            this.$refs.employeesPreviewChild.loading = false;
          }

          if (!error.response) {
            this.showMessage({
              mode: "error",
              text: "Unknown error. Please try again later"
            });
            return;
          } else if (error.response.status === 404) {
            this.showMessage({
              mode: "error",
              text:
                "Invalid file. Please upload a valid file based on provided Template."
            });
            return;
          } else if (error.response.status != 400) {
            //400 for validation errors, handled below
            this.showMessage({
              mode: "error",
              text: "Unknown error. Please try again later"
            });
            return;
          }

          const response = error.response.data;
          if (response && response.validationErrors) {
            this.previewEmployees = response.data;
            this.previewErrors = response.validationErrors;
            this.previewHasValid = response.hasValid;
          }
        }
      );
    }
  }
};
</script>

<style lang="sass" scoped>
.card-with-highlight.main-content
  padding-left: 72px !important
  padding-right: 72px !important
h1
  font-size: 40px
  line-height: 50px
  color: $darker-grey

h2
  font-size: 24px
  line-height: 30px
  color: $darker-grey

li
  font-size: 18px
  line-height: 23px
  color: #3A3A3A !important
  padding-left: 6px

ol.parenthetical-list > li::marker
  content: counter(list-item) ") "

ol.parenthetical-list > li li
  font-style: italic

.file-upload-dnd
  max-width: 450px

.download-button
  margin: 8px 0

.manual-upload
  font-size: 18px
  line-height: 23px
  padding-left: 8px
  color: #3A3A3A !important
  font-style: italic

.container .v-btn.v-btn--text.bold
  font-weight: 700

ol.parenthetical-list > li li::marker
  content: "•"
</style>
